import { ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './sticky-detail-header.module.css';

/* Match --header-height and --sticky-bar-height. */
const HEADER_HEIGHT = 72;
const STICKY_BAR_HEIGHT = 64;

interface StickyDetailHeaderProps {
    imageUrl?: string;
    title?: string;
    subtitle?: string;
    /* Renders subtitle next to the title instead of stacked below it. */
    inlineSubtitle?: boolean;
    meta?: ReactNode;
    renderActions?: () => ReactNode;
    targetRef: RefObject<HTMLElement>;
}

export function StickyDetailHeader({
    imageUrl,
    title,
    subtitle,
    inlineSubtitle,
    meta,
    renderActions,
    targetRef,
}: StickyDetailHeaderProps) {
    const [isVisible, setIsVisible] = useState(false);
    const isVisibleRef = useRef(false);

    useEffect(() => {
        const target = targetRef.current;
        if (!target) return undefined;

        const observer = new IntersectionObserver(
            ([entry]) => {
                const nextVisible = !entry.isIntersecting;

                if (nextVisible === isVisibleRef.current) return;

                isVisibleRef.current = nextVisible;
                setIsVisible(nextVisible);
            },
            {
                threshold: 0,
                // Show once the banner is behind the bar, so a tracklist header docks under it instead of jumping.
                rootMargin: `-${HEADER_HEIGHT + STICKY_BAR_HEIGHT}px 0px 0px 0px`,
            }
        );

        observer.observe(target);

        requestAnimationFrame(() => {
            const [entry] = observer.takeRecords();
            if (!entry) return;

            const nextVisible = !entry.isIntersecting;
            isVisibleRef.current = nextVisible;
            setIsVisible(nextVisible);
        });

        return () => {
            observer.disconnect();
            isVisibleRef.current = false;
        };
    }, [targetRef, title]);

    return (
        <div
            className={classNames(styles['sticky-detail-header'], {
                [styles['sticky-detail-header--visible']]: isVisible,
            })}
            aria-hidden={!isVisible}
        >
            {imageUrl && (
                <div className={styles['sticky-detail-header__image-container']}>
                    <img src={imageUrl} alt="" className={styles['sticky-detail-header__image']} draggable={false} />
                </div>
            )}
            <div
                className={classNames(styles['sticky-detail-header__text'], {
                    [styles['sticky-detail-header__text--inline']]: inlineSubtitle,
                })}
            >
                {title && <p className={styles['sticky-detail-header__title']}>{title}</p>}
                {subtitle && <p className={styles['sticky-detail-header__subtitle']}>{subtitle}</p>}
                {meta}
            </div>
            {isVisible && renderActions && (
                <div className={styles['sticky-detail-header__actions']}>{renderActions()}</div>
            )}
        </div>
    );
}
